import React, { useRef, useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SignatureCanvas from 'react-signature-canvas';
import Icon from '@/components/Common/Icons/Icon';
import clsx from 'clsx';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/dropdown';
import { Divider } from '@heroui/divider';
import { NextUI } from '@/components/NextUI';

interface SignatureCanvasProps {
  ref: React.RefObject<SignatureCanvas>;
  penColor: string;
  canvasProps: {
    className: string;
  };
  onEnd: () => void;
  onBegin: () => void;
  minWidth: number;
}

type SignatureCanvasType = SignatureCanvas & {
  penColor: string;
  clear: () => void;
  getCanvas: () => HTMLCanvasElement;
  _sigPad: {
    _handleMouseMove: (event: MouseEvent) => void;
    _handleMouseUp: (event: MouseEvent) => void;
  };
};

const CANVAS_READ_CONTEXT_OPTIONS: CanvasRenderingContext2DSettings = {
  willReadFrequently: true,
};

const DrawIt = ({
  signRef,
  onSignatureChange,
}: {
  signRef?: React.MutableRefObject<HTMLCanvasElement>;
  onSignatureChange: (hasContent: boolean) => void;
}) => {
  const sigCanvas = useRef<SignatureCanvasType>(null);
  const [strokeColor, setStrokeColor] = useState('#4D5DEB');
  const [history, setHistory] = useState<ImageData[]>([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const isUndoDisabled = currentStep < 0;
  const isRedoDisabled = currentStep >= history.length - 1;

  React.useEffect(() => {
    if (signRef && sigCanvas.current) {
      signRef.current = sigCanvas.current.getCanvas();
    }
  }, [signRef]);

  React.useEffect(() => {
    if (sigCanvas.current) {
      sigCanvas.current.penColor = strokeColor;
    }
  }, [strokeColor]);

  const saveCurrentState = useCallback(() => {
    if (sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext('2d', CANVAS_READ_CONTEXT_OPTIONS);
      if (!ctx) return;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext(
        '2d',
        CANVAS_READ_CONTEXT_OPTIONS
      );
      if (!tempCtx) return;

      tempCtx.fillStyle = 'transparent';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);

      const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);

      setHistory((prev) => [...prev.slice(0, currentStep + 1), imageData]);
      setCurrentStep((prev) => prev + 1);

      const hasContent = imageData.data.some(
        (channel, index) => index % 4 === 3 && channel > 0
      );
      setIsEmpty(!hasContent);
      onSignatureChange(hasContent);
    }
  }, [currentStep, onSignatureChange]);

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      saveCurrentState();
      setCurrentStep(-1);
      setHistory([]);
      setIsDrawing(false);
      setIsEmpty(true);
      onSignatureChange(false);
    }
  };

  const handleColorChange = (color: string) => {
    setStrokeColor(color);
    if (sigCanvas.current) {
      sigCanvas.current.penColor = color;
    }
  };

  const handleUndo = () => {
    if (!isUndoDisabled && sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext('2d', CANVAS_READ_CONTEXT_OPTIONS);
      if (ctx) {
        setCurrentStep((prev) => prev - 1);
        if (currentStep > 0) {
          ctx.putImageData(history[currentStep - 1], 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const hasContent = imageData.data.some((channel) => channel !== 0);
          setIsEmpty(!hasContent);
          onSignatureChange(hasContent);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          setIsDrawing(false);
          setIsEmpty(true);
          onSignatureChange(false);
        }
      }
    }
  };

  const handleRedo = () => {
    if (!isRedoDisabled && sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext('2d', CANVAS_READ_CONTEXT_OPTIONS);
      if (ctx) {
        setCurrentStep((prev) => prev + 1);
        ctx.putImageData(history[currentStep + 1], 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((channel) => channel !== 0);
        setIsEmpty(!hasContent);
        onSignatureChange(hasContent);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (sigCanvas.current && history.length > 0 && currentStep >= 0) {
        const canvas = sigCanvas.current.getCanvas();
        const ctx = canvas.getContext('2d', CANVAS_READ_CONTEXT_OPTIONS);
        if (ctx) {
          ctx.putImageData(history[currentStep], 0, 0);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [history, currentStep]);

  useEffect(() => {
    const sigPadInstance = sigCanvas.current?._sigPad;
    if (!sigPadInstance) return;

    const handleWindowMouseMove = (event: MouseEvent) => {
      sigPadInstance._handleMouseMove(event);
    };

    const handleWindowMouseUp = (event: MouseEvent) => {
      sigPadInstance._handleMouseUp(event);
      setIsDrawing(false);
    };

    if (isDrawing) {
      window.addEventListener('mousemove', handleWindowMouseMove);
      window.addEventListener('mouseup', handleWindowMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isDrawing]);

  const handleBeginDrawing = () => {
    setIsDrawing(true);
  };

  const handleEndDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    saveCurrentState();

    if (sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext('2d', CANVAS_READ_CONTEXT_OPTIONS);
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((channel) => channel !== 0);
        onSignatureChange(hasContent);
      }
    }
  };

  const signatureProps: SignatureCanvasProps = {
    ref: sigCanvas,
    penColor: strokeColor,
    canvasProps: {
      className: 'sigCanvas w-full h-full rounded-t-extra-lg z-[1000]',
    },
    onEnd: handleEndDrawing,
    onBegin: handleBeginDrawing,
    minWidth: 2,
  };

  return (
    <div className="flex flex-col w-full py-4 md:pl-6 !select-none">
      <div className="flex justify-between items-center w-full">
        <Dropdown className="!min-w-0">
          <DropdownTrigger>
            <NextUI.Button variant={'ghost'} className="!border-0">
              <div
                className="rounded-full w-4 h-4"
                style={{ backgroundColor: strokeColor }}
              ></div>
              <FontAwesomeIcon
                icon={['fas', 'chevron-down']}
                className="text-xs text-gray-medium"
              />
            </NextUI.Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Signature Colors">
            <DropdownItem
              key="blue"
              onClick={() => handleColorChange('#4D5DEB')}
            >
              <div className="bg-[#4D5DEB] rounded-full w-4 h-4"></div>
            </DropdownItem>
            <DropdownItem
              key="red"
              onClick={() => handleColorChange('#FF0000')}
            >
              <div className="bg-[#FF0000] rounded-full w-4 h-4"></div>
            </DropdownItem>
            <DropdownItem
              key="black"
              onClick={() => handleColorChange('#000000')}
            >
              <div className="bg-[#000000] rounded-full w-4 h-4"></div>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <div className="flex gap-4">
          <FontAwesomeIcon
            icon={['fas', 'rotate-left']}
            className={clsx(
              'text-xs w-4 h-4 p-2',
              isUndoDisabled
                ? 'text-gray-lighter'
                : 'text-gray-medium cursor-pointer'
            )}
            onClick={handleUndo}
          />
          <FontAwesomeIcon
            icon={['fas', 'rotate-right']}
            className={clsx(
              'text-xs w-4 h-4 p-2',
              isRedoDisabled
                ? 'text-gray-lighter'
                : 'text-gray-medium cursor-pointer'
            )}
            onClick={handleRedo}
          />
        </div>
      </div>
      <div className="bg-primary-extra-light-blue2 h-[233px] w-full rounded-extra-lg mt-5 relative">
        <div className="absolute top-0 left-8 z-[1010]">
          <Icon name="SignIcon" width={33} height={144} />
        </div>
        <div className="bg-transparent w-full h-full rounded-t-extra-lg relative z-[1000] overflow-hidden">
          <SignatureCanvas {...(signatureProps as any)} />
          {!isDrawing && isEmpty && (
            <span className="text-gray-light text-[13px] absolute bottom-20 left-1/2 transform -translate-x-1/2 select-none">
              Draw Here
            </span>
          )}
        </div>
        <div className="px-8 absolute bottom-4 w-full">
          <Divider />

          <div className="flex justify-end mt-4">
            <NextUI.Button
              variant={'link'}
              onClick={handleClear}
              className="!p-0 !px-0 !py-0 text-primary-blue !min-w-0  z-[1010]"
            >
              Clear
            </NextUI.Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawIt;
